import { useState, useCallback, useEffect, useRef } from "react";
import { Link } from "react-router";
import { useMutation, useLazyQuery } from "@apollo/client";
import { X, AlertTriangle } from "lucide-react";
import {
  CREATE_CLIENT,
  CREATE_PERSON,
  ADD_PERSON_TO_CLIENT,
  CHECK_DUPLICATE_EMAIL,
} from "../../apollo/clients";
import { useToastPush } from "../../components/ui/Toast";

interface AddClientModalProps {
  open: boolean;
  onClose: () => void;
  /** Called with the Client id (new client, or the existing one in contact mode). */
  onCreated?: (clientId: string) => void;
  /** When set, the modal adds a contact to this existing Client instead of
   *  creating a new relationship. */
  clientId?: string;
  /** Display name of the Client in contact mode (for the header). */
  clientName?: string;
}

interface DupMatch {
  personId: string;
  personName: string;
  clientId: string | null;
  clientName: string | null;
}

const DEBOUNCE_EMAIL_MS = 500;

export function AddClientModal({ open, onClose, onCreated, clientId, clientName }: AddClientModalProps) {
  const toast = useToastPush();
  const nameRef = useRef<HTMLInputElement>(null);
  const contactMode = !!clientId;

  const [name, setName] = useState("");
  const [alias, setAlias] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [title, setTitle] = useState("");

  const [dupMatch, setDupMatch] = useState<DupMatch | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // In client mode, a retry after "client created but contact failed" must
  // not mint a second Client — remember the one we already created.
  const createdClientIdRef = useRef<string | null>(null);

  const [checkDup] = useLazyQuery(CHECK_DUPLICATE_EMAIL, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      const matches = data?.allPeople ?? [];
      if (matches.length > 0) {
        const m = matches[0];
        const primary =
          (m.clients ?? []).find((c: { isPrimary: boolean }) => c.isPrimary) ??
          (m.clients ?? [])[0] ??
          null;
        setDupMatch({
          personId: m.id,
          personName: m.name ?? m.email ?? "this person",
          clientId: primary?.id ?? null,
          clientName: primary ? (primary.alias || primary.name) : null,
        });
      } else {
        setDupMatch(null);
      }
    },
  });

  const [createClientMut, { loading: creatingClient }] = useMutation(CREATE_CLIENT);
  // String form refetches the ACTIVE queries by operation name with their
  // own variables — a `{ query: GET_CLIENTS }` object with no variables
  // would run an unpaginated allClients into a cache entry nothing reads.
  const [createPersonMut, { loading: creatingPerson }] = useMutation(CREATE_PERSON, {
    refetchQueries: ["GetClients", "GetPeople"],
    awaitRefetchQueries: false,
  });
  const [attachPersonMut, { loading: attaching }] = useMutation(ADD_PERSON_TO_CLIENT, {
    refetchQueries: ["GetClients", "GetPeople"],
    awaitRefetchQueries: false,
  });
  const loading = creatingClient || creatingPerson || attaching;

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setName("");
      setAlias("");
      setEmail("");
      setPhone("");
      setBusinessName("");
      setTitle("");
      setDupMatch(null);
      setErrors({});
      createdClientIdRef.current = null;
      // Focus name field after modal renders
      requestAnimationFrame(() => nameRef.current?.focus());
    }
  }, [open]);

  // Debounce email duplicate check
  useEffect(() => {
    if (!email.trim() || !email.includes("@")) {
      setDupMatch(null);
      return;
    }
    const timer = setTimeout(() => {
      checkDup({ variables: { filter: { email: { eq: email.trim() } } } });
    }, DEBOUNCE_EMAIL_MS);
    return () => clearTimeout(timer);
  }, [email, checkDup]);

  const validate = useCallback((): boolean => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = "Name is required.";
    if (!email.trim()) {
      errs.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      errs.email = "Please enter a valid email address.";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [name, email]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!validate()) return;

      try {
        // Resolve the Client the contact goes into: an existing one
        // (contact mode), one created by an earlier failed attempt, or a
        // freshly created relationship named after the business/person.
        let targetClientId = clientId ?? createdClientIdRef.current;
        if (!targetClientId) {
          const { data: clientData } = await createClientMut({
            variables: {
              name: businessName.trim() || name.trim(),
              alias: alias.trim() || null,
            },
          });
          targetClientId = clientData?.createClient?.id as string;
          createdClientIdRef.current = targetClientId;
        }

        await createPersonMut({
          variables: {
            name: name.trim(),
            // The alias field only exists in client mode, where it belongs to
            // the Client record — the contact itself never gets one here.
            alias: null,
            email: email.trim(),
            phoneNumber: phone.trim() || null,
            businessName: businessName.trim() || null,
            title: title.trim() || null,
            clientId: targetClientId,
          },
        });
        toast({
          kind: "success",
          title: contactMode ? "Person added" : "Client added",
          description: contactMode
            ? `${name.trim()} has been added to ${clientName ?? "this client"}.`
            : `${businessName.trim() || name.trim()} has been added to your client list.`,
        });
        onClose();
        if (targetClientId) onCreated?.(targetClientId);
      } catch (err) {
        toast({
          kind: "error",
          title: contactMode ? "Failed to add contact" : "Failed to add client",
          description: (err as Error).message,
        });
      }
    },
    [
      name, alias, email, phone, businessName, title,
      clientId, clientName, contactMode,
      createClientMut, createPersonMut, validate, toast, onClose, onCreated,
    ],
  );

  // With M:N membership, "this email already exists" has a first-class
  // resolution: attach the existing person to the target client instead of
  // creating a duplicate person row.
  const handleAttachExisting = useCallback(async () => {
    if (!dupMatch) return;
    try {
      let targetClientId = clientId ?? createdClientIdRef.current;
      if (!targetClientId) {
        const { data: clientData } = await createClientMut({
          variables: {
            name: businessName.trim() || name.trim() || dupMatch.personName,
            alias: alias.trim() || null,
          },
        });
        targetClientId = clientData?.createClient?.id as string;
        createdClientIdRef.current = targetClientId;
      }
      await attachPersonMut({
        variables: { personId: dupMatch.personId, clientId: targetClientId },
      });
      toast({
        kind: "success",
        title: "Existing person attached",
        description: `${dupMatch.personName} was added${clientName ? ` to ${clientName}` : ""} without creating a duplicate.`,
      });
      onClose();
      if (targetClientId) onCreated?.(targetClientId);
    } catch (err) {
      toast({ kind: "error", title: "Failed to attach person", description: (err as Error).message });
    }
  }, [dupMatch, clientId, businessName, name, alias, clientName, createClientMut, attachPersonMut, toast, onClose, onCreated]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const heading = contactMode
    ? `Add contact${clientName ? ` to ${clientName}` : ""}`
    : "Add client";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label={heading}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-ink/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <form
        onSubmit={handleSubmit}
        className="relative bg-paper-hi rounded-lg shadow-lift w-full max-w-md mx-4 overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 hair-b">
          <h2 className="font-display text-[18px] font-medium truncate pr-4">{heading}</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 rounded hover:bg-paper-2 flex items-center justify-center text-ink-4"
            aria-label="Close"
          >
            <X size={14} strokeWidth={2} />
          </button>
        </div>

        {/* Form body */}
        <div className="px-6 py-5 space-y-4">
          {/* Name */}
          <div>
            <label htmlFor="add-client-name" className="block text-[12.5px] font-medium text-ink-2 mb-1.5">
              Name <span className="text-rust">*</span>
            </label>
            <input
              ref={nameRef}
              id="add-client-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full name"
              className={`w-full px-3 py-2 text-[13px] rounded-md bg-canvas hair border focus:outline-none focus:border-tide text-ink placeholder:text-ink-5 ${errors.name ? "!border-rust" : ""}`}
            />
            {errors.name && <p className="text-rust text-[11.5px] mt-1">{errors.name}</p>}
          </div>

          {/* Alias (relationship display-name override; hidden in contact mode) */}
          {!contactMode && (
            <div>
              <label htmlFor="add-client-alias" className="block text-[12.5px] font-medium text-ink-2 mb-1.5">
                Alias
              </label>
              <input
                id="add-client-alias"
                type="text"
                value={alias}
                onChange={(e) => setAlias(e.target.value)}
                placeholder="Display name override"
                className="w-full px-3 py-2 text-[13px] rounded-md bg-canvas hair border focus:outline-none focus:border-tide text-ink placeholder:text-ink-5"
              />
            </div>
          )}

          {/* Email */}
          <div>
            <label htmlFor="add-client-email" className="block text-[12.5px] font-medium text-ink-2 mb-1.5">
              Email <span className="text-rust">*</span>
            </label>
            <input
              id="add-client-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.com"
              className={`w-full px-3 py-2 text-[13px] rounded-md bg-canvas hair border focus:outline-none focus:border-tide text-ink placeholder:text-ink-5 ${errors.email ? "!border-rust" : ""}`}
            />
            {errors.email && <p className="text-rust text-[11.5px] mt-1">{errors.email}</p>}
            {dupMatch && !errors.email && (
              <div className="mt-1.5 text-[11.5px]">
                <div className="flex items-start gap-1.5 text-spark">
                  <AlertTriangle size={12} strokeWidth={2} className="mt-0.5 flex-shrink-0" />
                  <span>
                    This email already belongs to {dupMatch.personName}
                    {dupMatch.clientName && dupMatch.clientId ? (
                      <>
                        {" "}under{" "}
                        <Link
                          to={`/clients/${dupMatch.clientId}`}
                          className="underline hover:text-ink-2"
                        >
                          {dupMatch.clientName}
                        </Link>
                        .
                      </>
                    ) : (
                      "."
                    )}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={handleAttachExisting}
                  disabled={loading}
                  className="mt-1 ml-[18px] text-tide hover:underline disabled:opacity-60"
                >
                  {contactMode
                    ? `Attach the existing person${clientName ? ` to ${clientName}` : ""} instead`
                    : "Attach the existing person to this new client instead"}
                </button>
              </div>
            )}
          </div>

          {/* Phone */}
          <div>
            <label htmlFor="add-client-phone" className="block text-[12.5px] font-medium text-ink-2 mb-1.5">
              Phone
            </label>
            <input
              id="add-client-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(555) 123-4567"
              className="w-full px-3 py-2 text-[13px] rounded-md bg-canvas hair border focus:outline-none focus:border-tide text-ink placeholder:text-ink-5"
            />
          </div>

          {/* Business name + Title row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="add-client-business" className="block text-[12.5px] font-medium text-ink-2 mb-1.5">
                Business name
              </label>
              <input
                id="add-client-business"
                type="text"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                placeholder="Acme Inc."
                className="w-full px-3 py-2 text-[13px] rounded-md bg-canvas hair border focus:outline-none focus:border-tide text-ink placeholder:text-ink-5"
              />
              {!contactMode && (
                <p className="text-[11px] text-ink-5 mt-1">
                  When set, the client record is named after the business.
                </p>
              )}
            </div>
            <div>
              <label htmlFor="add-client-title" className="block text-[12.5px] font-medium text-ink-2 mb-1.5">
                Title
              </label>
              <input
                id="add-client-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Partner"
                className="w-full px-3 py-2 text-[13px] rounded-md bg-canvas hair border focus:outline-none focus:border-tide text-ink placeholder:text-ink-5"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 hair-t flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-md hair border text-[13px] text-ink-2 hover:bg-paper-2"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 rounded-md bg-tide text-paper-hi text-[13px] font-medium hover:bg-tide-hi disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading
              ? "Adding..."
              : contactMode
                ? "Add contact"
                : "Add client"}
          </button>
        </div>
      </form>
    </div>
  );
}
